import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useConfigStore } from '@core/stores/config'
import navItems from '@/navigation/vertical'
import { useStatisticsSettingsStore } from '@/stores/statistics/StatisticsSettings'
import { useUserStore } from '@/stores/UserStore'
import {
  resolveStatisticsPermission,
  handleEmployeesCommissionsGuard,
  handleServicesEmployeeGuard,
} from '@/plugins/statisticsRouteGuards'

// Recursively applies layout wrappers to routes that do not have children.
const recursiveLayouts = route => {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++) {
      route.children[i] = recursiveLayouts(route.children[i])
    }

    return route
  }

  return setupLayouts([route])[0]
}

const updatedRoutes = routes.map(route => recursiveLayouts(route))
const homeRedirect = { path: '/', redirect: '/calendar' }

// Finds the first enabled menu route that can be used as a safe redirect target.
const getFirstAvailableMenuRoute = menuItems =>
  Object.entries(menuItems)
    .map(([key, value]) => {
      if (!value) return null

      const nav = navItems.find(n => n.name === key)
      if (!nav || nav.href) return null

      return nav.to // menu key isn't always a route name (e.g. 'messages' -> 'communication')
    })
    .find(Boolean) || { name: 'help' }

// The whole /communication section is the 'messages' menu module
const getMenuKey = to => to.path.startsWith('/communication') ? 'messages' : to.name

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [homeRedirect, ...updatedRoutes],
  // Restores saved position, supports hash scrolling, otherwise scrolls to top.
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) return savedPosition
    if (to.path === from.path) return false
    if (to.hash) return { el: to.hash, behavior: 'smooth', top: 60 }

    return { top: 0 }
  },
})

router.beforeEach(async (to, from) => {
  // Show global loading indicator while route guards are evaluated.
  const userStore = useUserStore()
  const configStore = useConfigStore()
  configStore.showAppLoadingIndicator = true // SHOW TOP LOADER

  // Calendar settings are required for menu availability and employee validations.
  const calendarStore = useCalendarStore()
  await calendarStore.fetchCalendarSettings()

  // Statistics section has additional ACL rules based on settings permissions.
  if (to.path.startsWith('/statistics')) {

    const settingsStore = useStatisticsSettingsStore()

    if (!settingsStore.settings?.areLoaded) {
      await settingsStore.loadSettings()
    }

    const requiredPermission = resolveStatisticsPermission(to.path)

    if (requiredPermission) {
      const { hasAccess, isSelfMode } = settingsStore.getPermissions(requiredPermission)

      // Block users without permission from entering guarded statistics pages.
      if (!hasAccess) return { name: 'statistics' }

      // 👉 Finanse pracowników
      const employeesCommissionsResult = handleEmployeesCommissionsGuard({
        to,
        isSelfMode,
        userStore,
      })

      if (employeesCommissionsResult !== true) return employeesCommissionsResult

      // 👉 Usługi
      const servicesEmployeeResult = handleServicesEmployeeGuard({
        to,
        isSelfMode,
        userStore,
        calendarStore,
      })

      if (servicesEmployeeResult !== true) return servicesEmployeeResult

    }
  }

  // Redirect from disabled modules to the first available section.
  const menuKey = getMenuKey(to)
  if (menuKey !== 'help' && menuKey in calendarStore.menuItems && calendarStore.menuItems[menuKey] === false) {
    return getFirstAvailableMenuRoute(calendarStore.menuItems)
    // TODO: Odkomentuj jak dojdziemy do logowania przez FE - query: { redirect: to.fullPath }
  }

  return true
})

router.afterEach(to => {
  // Pages set their own title (TabHeader, or useTitle) - reset so none of them inherits the previous one
  //document.title = 'Saloner 2.0'

  // Statistics pages get a dedicated print layout (see styles/customs/print.scss)
  document.body.classList.toggle('print-stats', to.path.startsWith('/statistics'))


  // Small delay keeps transitions smoother before hiding the global loader.
  const configStore = useConfigStore()
  setTimeout(() => {
    configStore.showAppLoadingIndicator = false
  }, 450)
})

export { router }
export default app => {
  app.use(router)
}
